import { MenuUnfoldOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { Dropdown, Menu } from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import './AnalyticsChartSection.scss';

Highcharts.setOptions({
  colors: ['#a0e786', '#75c199', '#ffe783', '#ff644e', '#99d9ff', '#e7b0bd'],
});

interface ChartDataItem {
  name: string;
  value: number;
}

interface AnalyticsChartSectionProps {
  data: ChartDataItem[];
  chartType?: 'pie' | 'bar';
  name: string;
  type: string;
  unit: string;
  loading?: boolean;
  showUnit?: boolean;
  xAxisName?: string;
}

const AnalyticsChartSection: React.FC<AnalyticsChartSectionProps> = ({
  data,
  chartType = 'bar',
  name,
  type,
  unit,
  loading = false,
  showUnit = true,
  xAxisName = '',
}) => {
  const { t } = useTranslate();
  const chartRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    content: () => chartRef.current,
    documentTitle: `${name}-chart`,
  });

  const translatedData = data.map(item => ({
    name: t(item.name),
    y: item.value,
  }));

  const displayUnit = showUnit ? ` ${unit}` : '';
  const categories = translatedData.map(item => item.name);

  const options: Highcharts.Options =
    chartType === 'pie'
      ? {
          chart: {
            type: 'pie',
            className: 'pie-chart',
						plotShadow: false,
            style: {
              fontFamily: 'myGramaticaRegular',
							fontSize: '16px',
							color: '#000000',
            },
          },
          title: {
            text: name,
            align: 'center',
            style: {
              fontFamily: 'myGramaticaMedium',
							fontSize: '19px',
							color: '#000000',
            },
          },
          tooltip: {
            pointFormat: `<b>{point.name}: {point.y:.2f}${displayUnit}</b>`,
          },
          plotOptions: {
            pie: {
              allowPointSelect: true,
              cursor: 'pointer',
              dataLabels: {
                enabled: true,
                format: '<span style="font-size: 16px!important; font-family: myGramaticaRegular"><b >{point.name}</b>: {point.percentage:.1f} %</span>',
                style: {
                  fontFamily: 'myGramaticaRegular',
									fontSize: '16px',
									color: '#000000',
                },
              },
            },
          },
          series: [
            {
              type: 'pie',
              name: showUnit ? unit : undefined,
              data: translatedData,
            },
          ],
          credits: { enabled: false },
        }
      : {
          chart: {
            type: 'column',
            style: {
              fontFamily: 'myGramaticaRegular',
							fontSize: '16px',
							color: '#000000',
            },
          },
          title: {
            text: name,
            align: 'center',
            style: {
              fontFamily: 'myGramaticaMedium',
							fontSize: '19px',
							color: '#000000',
            },
          },
          xAxis: {
            categories,
            title: {
              text: xAxisName ? `<div class='chatNameX'>
                <span style="width: 12px; height: 12px; background-color: #c4c4c4; border-radius: 50%; display: inline-block; margin-right: 6px;"></span>
                <span>${xAxisName}</span>
              </div>`: '',
              align: 'middle',
              style: {
                fontFamily: 'myGramaticaRegular',
								fontSize: '16px',
								color: '#000000',
              },
              useHTML: true,
            },
            crosshair: true,
            labels: {
              overflow: 'allow',
              style: {
                  fontFamily: 'myGramaticaRegular',
									fontSize: '16px!important',
									color: '#000000',
              },
            },
          },
          responsive: {
            rules: [
              {
                condition: { maxWidth: 800 }, // mobile breakpoint
                chartOptions: {
                  xAxis: {
                    labels: {
                      step: categories.length > 12 ? 2 : 1,
                    },
                  },
                },
              },
            ],
          },
          yAxis: {
            min: 0,
            title: {
              text: unit,
              style: {
                fontFamily: 'myGramaticaRegular',
                fontSize: '16px!important',
                color: '#000000',
              },
            },
            labels: {
              style: {
                fontFamily: 'myGramaticaRegular',
                fontSize: '16px!important',
                color: '#000000',
              },
            },
          },
          tooltip: {
            pointFormat: `<span style="font-weight: bold;">{point.y:.2f}${displayUnit}</span>`,
          },
          plotOptions: {
            column: {
              pointPadding: 0.2,
              borderWidth: 0,
              colorByPoint: true,
              dataLabels: {
                enabled: true,
                format: `{point.y:.1f}${displayUnit}`,
                style: {
                  fontFamily: 'myGramaticaRegular',
                  fontSize: '16px!important',
                  color: '#000000',
                },
              },
            },
          },
          series: [
            {
              name: showUnit ? unit : undefined,
              type: 'column',
              colorByPoint: true,
              data: translatedData,
            },
          ],
          credits: { enabled: false },
          legend: { enabled: false },
        };

  const menu = (
    <Menu
      items={[
        {
          key: 'print',
          label: 'Download',
          onClick: handlePrint,
        },
      ]}
    />
  );

  return (
    <div
      className='analytics-chart-section-container'
      style={{ width: '100%' }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}
      >
        <Dropdown overlay={menu} trigger={['click']}>
          <MenuUnfoldOutlined
            style={{
              fontSize: 20,
              cursor: 'pointer',
              padding: 6,
              border: '1px solid #ccc',
              borderRadius: 4,
            }}
          />
        </Dropdown>
      </div>

      <div ref={chartRef}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    </div>
  );
};

export default AnalyticsChartSection;
